/**
 * Custom Option Fees
 * 
 * Handles adding fee products to cart when custom options are selected.
 * Fee products are linked to their parent via hidden properties.
 */

import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CustomOptionFeesManager {
  constructor() {
    this.pendingFees = new Map();
    this.processing = false;
    this.processedEvents = new Set(); // Track processed events to prevent duplicates
    this.init();
  }

  init() {
    // Only attach to product forms directly (not document/body to avoid duplicates)
    this.attachToProductForms();
    
    document.addEventListener('shopify:section:load', () => {
      // Re-attach to product forms after section reload
      this.attachToProductForms();
      this.pendingFees.clear();
    });
  }

  /**
   * Attach event listeners directly to product form elements
   */
  attachToProductForms() {
    const productForms = document.querySelectorAll('product-form-component');
    
    productForms.forEach(form => {
      if (form.dataset.feesAttached === 'true') {
        return; // Skip if already attached
      }
      
      // Create a bound handler so we can reference it later
      const handler = this.handleCartAdd.bind(this);
      form.addEventListener(ThemeEvents.cartUpdate, handler, { once: false });
      form.dataset.feesAttached = 'true';
    });
  }

  /**
   * Handle cart add event and check for fees
   * @param {CartAddEvent} event 
   */
  async handleCartAdd(event) {
    // Only process if this is a product-form add (not other cart updates)
    if (event.detail.data?.source !== 'product-form-component') {
      return;
    }
    if (event.detail.data?.didError) {
      return;
    }

    const productId = event.detail.data?.productId;
    const variantId = event.detail.data?.variantId;
    let parentCartLineKey = event.detail.data?.cartLineKey;
    let quantity = event.detail.data?.itemCount || 1;
    
    if (!productId) {
      return;
    }

    // Create a unique identifier for this cart add event
    const eventId = `${productId}-${Date.now()}`;
    
    // Check if we've already processed this event (within the last second)
    if (this.processedEvents.has(eventId)) {
      return;
    }
    
    if (this.processing) {
      return;
    }
    
    // Mark this event as processed
    this.processedEvents.add(eventId);
    // Clean up old processed events after 2 seconds
    setTimeout(() => {
      this.processedEvents.delete(eventId);
    }, 2000);

    let productForm = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;

    if (!productForm || productForm.tagName.toLowerCase() !== 'product-form-component') {
      productForm = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
    }
    
    if (!productForm) {
      return;
    }

    const form = productForm.querySelector('form');
    if (!form) {
      return;
    }

    const formData = new FormData(form);
    const submittedProperties = {};
    for (const [name, value] of formData.entries()) {
      const property = name.match(/^properties\[(.*)\]$/);
      if (property && typeof value === 'string' && value !== '') submittedProperties[property[1]] = value;
    }
    // The product form can reset or clamp its input while refreshing cart stock.
    // Use the confirmed added quantity from the event before reading that input.
    const addedQuantity = Number(event.detail.data?.itemCount);
    const submittedQuantity = parseInt(formData.get('quantity'), 10);
    if (Number.isInteger(addedQuantity) && addedQuantity > 0) {
      quantity = addedQuantity;
    } else if (!isNaN(submittedQuantity) && submittedQuantity > 0) {
      quantity = submittedQuantity;
    }

    if (!parentCartLineKey) {
      parentCartLineKey = await this.findParentCartLineKey(productId, variantId);
    }

    // Keep the identity of the design, because Shopify can change cart line keys.
    const parent = event.detail.resource?.items?.find((item) =>
      String(item.product_id) === String(productId) &&
      (!variantId || String(item.variant_id) === String(variantId)) &&
      Object.entries(submittedProperties).every(([key, value]) => item.properties?.[key] === value)
    );
    if (parent) parentCartLineKey = parent.key;
    const parentProperties = parent?.properties || submittedProperties;

    // Collect all fee products that should be added
    const feesToAdd = await this.collectFees(form, productId, quantity, parentCartLineKey);

    for (const fee of feesToAdd) {
      if (variantId) {
        fee.properties._fee_parent_variant_id = String(variantId);
        fee.properties._fee_parent_properties = JSON.stringify(parentProperties);
      }
    }

    if (feesToAdd.length > 0) {
      this.processing = true;
      try {
        await this.addFeesToCart(feesToAdd);
      } catch (error) {
        console.error('Could not add custom option fees:', error);
      } finally {
        this.processing = false;
      }
    }
  }

  /**
   * Collect all fees that should be added based on form values
   * @param {HTMLFormElement} form 
   * @param {string} parentProductId 
   * @param {number} productQuantity - Quantity of the parent product being added
   * @param {string|undefined} parentCartLineKey - Cart line key for the parent product
   * @returns {Promise<Array<{variantId: string, quantity: number, properties: Object}>>}
   */
  async collectFees(form, parentProductId, productQuantity, parentCartLineKey, feeRoot, propertyValues) {
    const fees = [];
    // Check all custom option blocks for fees
    const blocks = feeRoot
      ? Array.from(feeRoot.querySelectorAll('[data-fee-config]'))
      : this.getFeeBlocks(form);
    
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const feeConfig = this.parseFeeConfig(block);
      const blockType = block.dataset.feeType;
      const propertyKey = block.dataset.key || block.dataset.feePropertyKey;
      const propertyValue = propertyValues && Object.prototype.hasOwnProperty.call(propertyValues, propertyKey)
        ? propertyValues[propertyKey]
        : this.getPropertyValue(form, propertyKey, block);
      
      if (!feeConfig || !feeConfig.enabled) {
        continue;
      }

      switch (blockType) {
        case 'checkbox': {
          // For checkboxes, check the checkbox element directly
          const checkbox = block.querySelector('input[type="checkbox"]');

          if ((propertyValues && propertyValue) || (!propertyValues && checkbox && checkbox.checked)) {
            const value = propertyValues ? propertyValue : checkbox.value || 'checked';
            const fee = await this.createFee(feeConfig, parentProductId, propertyKey, value, productQuantity, parentCartLineKey);
            if (fee) fees.push(fee);
          }
          break;
        }
        
        case 'text': {
          // For text inputs, check if there's a value
          const value = propertyValue;
          
          if (value && value.toString().trim() !== '') {
            const fee = await this.createFee(feeConfig, parentProductId, propertyKey, value, productQuantity, parentCartLineKey);
            if (fee) fees.push(fee);
          }
          break;
        }

        case 'select':
        case 'radio': {
          // For select/radio, check if selected option has a fee
          const value = propertyValue;
          if (value && feeConfig.optionFees) {
            const optionFee = propertyValues
              ? this.lookupOptionFee(feeConfig.optionFees, value)
              : this.getSelectedInputFeeConfig(block) || this.lookupOptionFee(feeConfig.optionFees, value);
            if (optionFee) {
              const fee = await this.createFee(optionFee, parentProductId, propertyKey, value, productQuantity, parentCartLineKey);
              if (fee) fees.push(fee);
            }
          }
          break;
        }

        case 'multiselect':
        case 'swatch-checkbox': {
          const checkedFeeInputs = block.querySelectorAll('input[data-fee-handle]:checked');
          if (!propertyValues && checkedFeeInputs.length > 0) {
            for (const input of checkedFeeInputs) {
              const optionFee = this.getFeeConfigFromInput(input);
              if (optionFee) {
                const fee = await this.createFee(
                  optionFee,
                  parentProductId,
                  propertyKey,
                  input.value || optionFee.handle,
                  productQuantity,
                  parentCartLineKey
                );
                if (fee) fees.push(fee);
              }
            }
            break;
          }

          // For multiselect/checkboxes, check each selected option
          const value = propertyValue;
          if (value && feeConfig.optionFees) {
            // Value is comma-separated list
            const selectedOptions = value.toString().split(',').map((entry) => this.normalizeOptionKey(entry)).filter(Boolean);
            for (const option of selectedOptions) {
              const optionFeeConfig = this.lookupOptionFee(feeConfig.optionFees, option);
              if (optionFeeConfig) {
                const fee = await this.createFee(
                  optionFeeConfig,
                  parentProductId,
                  propertyKey,
                  option,
                  productQuantity,
                  parentCartLineKey
                );
                if (fee) {
                  fees.push(fee);
                }
              }
            }
          }
          break;
        }

        case 'file': {
          // For file upload, check if a file was selected
          const fileInput = block.querySelector('input[type="file"]');
          const hasFileValue = propertyValues
            ? propertyValue && propertyValue.toString().trim() !== ''
            : fileInput?.files?.length > 0;
          if (hasFileValue) {
            const value = propertyValues ? propertyValue : fileInput.files.length + ' file(s)';
            const fee = await this.createFee(feeConfig, parentProductId, propertyKey, value, productQuantity, parentCartLineKey);
            if (fee) {
              fees.push(fee);
            }
          }
          break;
        }

        case 'personalization': {
          const textKey = block.dataset.personalizationTextKey;
          const textValue = textKey
            ? propertyValues && Object.prototype.hasOwnProperty.call(propertyValues, textKey)
              ? propertyValues[textKey]
              : this.getPropertyValue(form, textKey, block)
            : null;
          const fileInput = block.querySelector('input[type="file"]');
          const hasText = textValue && textValue.toString().trim() !== '';
          const imageKey = block.dataset.personalizationImageKey;
          const savedImageValue = imageKey && propertyValues ? propertyValues[imageKey] : '';
          const hasFile = propertyValues
            ? savedImageValue && savedImageValue.toString().trim() !== ''
            : fileInput?.files?.length > 0;

          if (hasText || hasFile) {
            const fee = await this.createFee(
              feeConfig,
              parentProductId,
              propertyKey || textKey || 'Personalization',
              hasText ? textValue : propertyValues ? savedImageValue : fileInput.files.length + ' file(s)',
              productQuantity,
              parentCartLineKey
            );
            if (fee) {
              fees.push(fee);
            }
          }
          break;
        }
      }
    }

    return fees;
  }

  /**
   * Build fee line items for the global customization fields in a Bulk Order.
   * The same block configuration and selected values are used by regular
   * product forms; only the parent scope changes from one product to the order.
   * @param {HTMLElement} feeRoot
   * @param {string} parentProductId
   * @param {number} productQuantity
   * @param {string|undefined} parentCartLineKey
   * @param {string} parentGroup
   * @param {Object} propertyValues
   * @returns {Promise<Array<{id: string, quantity: number, properties: Object}>>}
   */
  async collectBulkOrderFees(feeRoot, parentProductId, productQuantity, parentCartLineKey, parentGroup, propertyValues) {
    if (!feeRoot) return [];

    const syntheticForm = document.createElement('form');
    const fees = await this.collectFees(
      syntheticForm,
      parentProductId,
      productQuantity,
      parentCartLineKey,
      feeRoot,
      propertyValues
    );

    fees.forEach((fee) => {
      fee.properties = fee.properties || {};
      fee.properties._fee_parent_group = parentGroup;
    });

    return fees;
  }

  /**
   * Find fee-enabled blocks that are either inside the form or associated to it
   * with a form id.
   * @param {HTMLFormElement} form
   * @returns {HTMLElement[]}
   */
  getFeeBlocks(form) {
    const blocks = new Set(form.querySelectorAll('[data-fee-config]'));

    const formId = form.getAttribute('id');
    if (formId) {
      const escapedFormId = window.CSS && CSS.escape ? CSS.escape(formId) : formId;
      document.querySelectorAll(`[data-fee-config][data-form="${escapedFormId}"]`).forEach(block => {
        blocks.add(block);
      });
    }

    return Array.from(blocks);
  }

  /**
   * Normalize option labels for fee lookup (handles NBSP and odd spacing).
   * @param {string} value
   * @returns {string}
   */
  normalizeOptionKey(value) {
    if (value == null || value === '') return '';

    return value
      .toString()
      .replace(/\u00a0/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  normalizeOptionSlug(value) {
    return this.normalizeOptionKey(value).replace(/\s+/g, '_');
  }

  /**
   * @param {Object} optionFees
   * @param {string} value
   * @returns {Object|null}
   */
  lookupOptionFee(optionFees, value) {
    if (!optionFees || !value) return null;

    const key = this.normalizeOptionKey(value);
    if (key && optionFees[key]) {
      return optionFees[key];
    }

    const slug = this.normalizeOptionSlug(value);
    if (slug && optionFees[slug]) {
      return optionFees[slug];
    }

    for (const [optionKey, optionFee] of Object.entries(optionFees)) {
      if (this.normalizeOptionKey(optionKey) === key || this.normalizeOptionSlug(optionKey) === slug) {
        return optionFee;
      }
    }

    return null;
  }

  /**
   * @param {string} left
   * @param {string} right
   * @returns {boolean}
   */
  optionValuesMatch(left, right) {
    return this.normalizeOptionKey(left) === this.normalizeOptionKey(right);
  }

  /**
   * Read a line item property value from the form, including controls that are
   * rendered outside the form but linked with form="...".
   * @param {HTMLFormElement} form
   * @param {string} propertyKey
   * @param {HTMLElement} block
   * @returns {string}
   */
  getPropertyValue(form, propertyKey, block) {
    if (!propertyKey) return '';

    const fieldName = `properties[${propertyKey}]`;

    if (block.classList?.contains('design-selector')) {
      const selectorValue = this.getDesignSelectorControlValue(block, fieldName);
      if (selectorValue) {
        return selectorValue;
      }
    }

    const formData = new FormData(form);
    const formValues = formData.getAll(fieldName).filter((value) => value?.toString().trim() !== '');

    if (formValues.length > 0) {
      return formValues.join(', ');
    }

    const controls = this.getPropertyControls(form, fieldName, block);
    const values = [];

    controls.forEach((control) => {
      if (control.disabled) return;

      if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) {
        return;
      }

      if (control.value?.toString().trim() !== '') {
        values.push(control.value);
      }
    });

    if (values.length > 0) {
      return values.join(', ');
    }

    const designValue = this.getDesignSelectorValue(block);
    if (designValue) {
      return designValue;
    }

    return '';
  }

  /**
   * Read the current Product Design Selector value from its own controls first.
   * @param {HTMLElement} block
   * @param {string} fieldName
   * @returns {string}
   */
  getDesignSelectorControlValue(block, fieldName) {
    const hiddenInput = Array.from(block.querySelectorAll('input[type="hidden"]')).find(
      (input) => input.name === fieldName
    );
    if (hiddenInput && !hiddenInput.disabled && hiddenInput.value?.toString().trim() !== '') {
      return hiddenInput.value.toString().trim();
    }

    const checkedValues = Array.from(block.querySelectorAll('.design-selector__input:checked'))
      .filter((input) => !input.disabled)
      .map((input) => input.value?.toString().trim())
      .filter(Boolean);

    return checkedValues.join(', ');
  }

  /**
   * Product Design Selector can be driven by Hat Lab state before its radio
   * value is synced. Use the same selected design source as the display block.
   * @param {HTMLElement} block
   * @returns {string}
   */
  getDesignSelectorValue(block) {
    if (!block.classList?.contains('design-selector')) return '';

    try {
      const selectedDesign = window.HATLAB_DESIGN?.getSelectedDesign?.(block);
      return selectedDesign?.title?.toString().trim() || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Read fee metadata directly from the selected option input when present.
   * @param {HTMLElement} block
   * @returns {Object|null}
   */
  getSelectedInputFeeConfig(block) {
    let selectedInput = block.querySelector('input[data-fee-handle]:checked');
    if (!selectedInput && block.classList?.contains('design-selector')) {
      const fieldName = `properties[${block.dataset.key || ''}]`;
      const selectorValue = this.getDesignSelectorControlValue(block, fieldName);
      if (selectorValue) {
        const selectedValues = selectorValue.split(',').map((value) => value.trim());
        selectedInput = Array.from(block.querySelectorAll('input[data-fee-handle]')).find(
          (input) => selectedValues.some((value) => this.optionValuesMatch(input.value, value))
        );
      }

      if (!selectedInput) {
        const designValue = this.getDesignSelectorValue(block);
        if (designValue) {
          selectedInput = Array.from(block.querySelectorAll('input[data-fee-handle]')).find(
            (input) => this.optionValuesMatch(input.value, designValue)
          );
        }
      }
    }
    if (!selectedInput) return null;

    return this.getFeeConfigFromInput(selectedInput);
  }

  /**
   * Read fee metadata directly from an option input when present.
   * @param {HTMLInputElement} input
   * @returns {Object|null}
   */
  getFeeConfigFromInput(input) {
    if (!input) return null;

    const handle = input.dataset.feeHandle?.trim();
    if (!handle) return null;

    const config = {
      handle,
      type: input.dataset.feeType || 'one-time'
    };

    if (input.dataset.feeMinQtyWaive) {
      config.minQtyWaive = input.dataset.feeMinQtyWaive;
    }

    if (input.dataset.feeVariantId) {
      config.variantId = input.dataset.feeVariantId;
    }

    if (input.dataset.feePrice) {
      const price = Number(input.dataset.feePrice);
      if (!Number.isNaN(price)) {
        config.price = price;
      }
    }

    return config;
  }

  /**
   * Find controls for a property inside the block, the form, or associated by
   * matching form id.
   * @param {HTMLFormElement} form
   * @param {string} fieldName
   * @param {HTMLElement} block
   * @returns {HTMLElement[]}
   */
  getPropertyControls(form, fieldName, block) {
    const controls = new Set();
    const candidates = [
      ...Array.from(form.querySelectorAll('input, select, textarea')),
      ...Array.from(block.querySelectorAll('input, select, textarea'))
    ];

    const formId = form.getAttribute('id');
    if (formId) {
      document.querySelectorAll('input, select, textarea').forEach((control) => {
        if (control.getAttribute('form') === formId) {
          candidates.push(control);
        }
      });
    }

    candidates.forEach((control) => {
      if (control.name === fieldName) {
        controls.add(control);
      }
    });

    return Array.from(controls);
  }

  /**
   * Parse fee configuration from block element
   * @param {HTMLElement} block 
   * @returns {Object|null}
   */
  parseFeeConfig(block) {
    try {
      const jsonScript = block.querySelector('script[data-fee-config-json]');
      if (jsonScript?.textContent?.trim()) {
        return JSON.parse(jsonScript.textContent);
      }

      const rawConfig = block.dataset.feeConfig || '{}';
      return JSON.parse(rawConfig);
    } catch (e) {
      return null;
    }
  }

  /**
   * Create a fee object from configuration
   * @param {Object} config - Fee configuration object
   * @param {string} parentProductId 
   * @param {string} propertyKey 
   * @param {string} optionValue 
   * @param {number} productQuantity - Quantity of parent product being added
   * @param {string|undefined} parentCartLineKey - Cart line key for the parent product
   * @returns {Promise<Object|null>}
   */
  async createFee(config, parentProductId, propertyKey, optionValue, productQuantity, parentCartLineKey) {
    // Handle both old format (string) and new format (object)
    let productHandle, feeType, minQtyWaive, variantId;
    
    if (typeof config === 'string') {
      // Old format: just the product handle
      productHandle = config;
      feeType = 'one-time';
      minQtyWaive = null;
    } else {
      // New format: object with handle, type, and optional minQtyWaive
      productHandle = config.handle || config.productHandle;
      variantId = config.variantId || config.variant_id;
      feeType = (config.type || 'one-time').toLowerCase().trim();
      minQtyWaive = config.minQtyWaive || null;
    }
    
    if (!productHandle && !variantId) return null;

    if (variantId != null && variantId !== '') {
      variantId = String(variantId);
    }

    // Check if fee should be waived based on quantity threshold
    if (minQtyWaive) {
      const threshold = parseInt(minQtyWaive, 10);
      if (!isNaN(threshold) && productQuantity >= threshold) {
        return null; // Skip this fee
      }
    }

    try {
      if (!variantId) {
        // Fetch the product to get its variant ID when Liquid did not provide it.
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          return null;
        }

        const product = await response.json();

        if (!product.variants || product.variants.length === 0) {
          return null;
        }

        // Use first available variant
        variantId = product.variants[0].id;
      }

      // Calculate fee quantity based on type (case-insensitive)
      let feeQuantity = 1;
      if (feeType === 'per-item') {
        feeQuantity = productQuantity;
      }

      const feeItem = {
        id: variantId,
        quantity: feeQuantity,
        properties: {
          _fee_parent_product_id: parentProductId,
          _fee_property_key: propertyKey || '',
          _fee_quantity_mode: feeType === 'per-item' ? 'per-item' : 'one-time'
        }
      };

      if (parentCartLineKey) {
        feeItem.properties._fee_parent_key = parentCartLineKey;
      }
      
      return feeItem;
    } catch (error) {
      return null;
    }
  }

  /**
   * Find the most recent cart line for the parent product when the add response
   * did not include a cart line key.
   * @param {string} parentProductId
   * @param {string|undefined} variantId
   * @returns {Promise<string|undefined>}
   */
  async findParentCartLineKey(parentProductId, variantId) {
    try {
      const response = await fetch(window.Shopify?.routes?.root + 'cart.js');
      if (!response.ok) return undefined;

      const cart = await response.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const matchingItems = items.filter((item) => {
        const productMatches = item.product_id?.toString() === parentProductId.toString();
        const variantMatches = !variantId || item.variant_id?.toString() === variantId.toString();
        return productMatches && variantMatches && item.key;
      });

      return matchingItems.length > 0 ? matchingItems[0].key : undefined;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * Add multiple fees to cart
   * @param {Array} fees
   * @param {{refreshCart?: boolean}} [options]
   */
  async addFeesToCart(fees, options = {}) {
    try {
      const sectionsToUpdate = new Set();
      document.querySelectorAll('cart-items-component').forEach((cartItems) => {
        if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
          sectionsToUpdate.add(cartItems.dataset.sectionId);
        }
      });

      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: fees,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname
          })
        })
      );

      if (!addResponse.ok) {
        const addError = await addResponse.json().catch(() => ({}));
        throw new Error(addError.description || 'Fee cart add failed');
      }

      const addData = await addResponse.json();
      if (options.refreshCart === false) {
        return addData;
      }

      const cartResponse = await fetch(window.Shopify?.routes?.root + 'cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'custom-option-fees', {
          source: 'custom-option-fees',
          itemCount: cart.item_count,
          sections: addData.sections || {}
        })
      );
    } catch (error) {
      throw error;
    }
  }
}

// Initialize the manager after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.customOptionFeesManager) {
      window.customOptionFeesManager = new CustomOptionFeesManager();
    }
  });
} else {
  if (!window.customOptionFeesManager) {
    window.customOptionFeesManager = new CustomOptionFeesManager();
  }
}
